var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useCurrentSeasonContext } from "@nitra/nkmb-card-components/contexts/CurrentSeasonContext";
import { useSeasonSettings } from "@nitra/nkmb-card-components/contexts/SeasonContextSettingsProvider";
import { useUrlParams } from "@nitra/nkmb-card-components/contexts/UrlParamsContextProvider";
import { createContext, useContext, useEffect, useRef, useState } from "react";
var ALL_SEASONS = 42;
var SeasonContext = createContext(null);
export function useSeasonContext() {
    var context = useContext(SeasonContext);
    if (context === null) {
        throw new Error('useSeasonContext must be used within a SeasonContextProvider');
    }
    return context;
}
export function SeasonContextProvider(_a) {
    var children = _a.children;
    var currentSeasonNumber = useCurrentSeasonContext().currentSeasonNumber;
    var _b = useUrlParams(), getParam = _b.getParam, setParam = _b.setParam;
    var _c = useSeasonSettings(), allowAllSeasons = _c.allowAllSeasons, availableSeasonsOverride = _c.availableSeasonsOverride, firstSeasonOverride = _c.firstSeasonOverride, lastSeasonOverride = _c.lastSeasonOverride;
    // Compute the first and last seasons
    var firstSeason = firstSeasonOverride !== null && firstSeasonOverride !== void 0 ? firstSeasonOverride : 1960;
    var lastSeason = lastSeasonOverride !== null && lastSeasonOverride !== void 0 ? lastSeasonOverride : Math.floor(currentSeasonNumber / 10000);
    var _d = useState([]), availableSeasons = _d[0], setAvailableSeasons = _d[1];
    var _e = useState(null), season = _e[0], _setSeason = _e[1];
    var isSeasonInitialized = useRef(false);
    var seasonSelectedManually = useRef(false);
    // Recalculate available seasons when settings change
    useEffect(function () {
        var seasons = [];
        if (availableSeasonsOverride && availableSeasonsOverride.length > 0) {
            seasons = __spreadArray([], availableSeasonsOverride, true);
        }
        else {
            for (var year = firstSeason; year <= lastSeason; year++) {
                seasons.push(year);
            }
        }
        if (allowAllSeasons) {
            // Prepend ALL_SEASONS to the list
            seasons.unshift(ALL_SEASONS);
            if (!seasonSelectedManually.current) {
                _setSeason(ALL_SEASONS);
            }
        }
        setAvailableSeasons(seasons);
        // Adjust season if necessary
        if (isSeasonInitialized.current) {
            if (season !== null && !seasons.includes(season)) {
                // Current season is no longer valid
                var newSeason = allowAllSeasons ? ALL_SEASONS : seasons[0];
                _setSeason(newSeason);
                seasonSelectedManually.current = false;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowAllSeasons, availableSeasonsOverride, firstSeason, lastSeason]);
    // Initialize season based on URL or default
    useEffect(function () {
        if (!isSeasonInitialized.current && availableSeasons.length > 0) {
            var initialSeason = availableSeasons[0];
            var seasonParam = getParam('season');
            if (seasonParam !== null) {
                var parsedSeason = seasonParam === 'ALL' ? ALL_SEASONS : Number(seasonParam);
                // this could potentially help with https://youtrack.jimthe.com/issue/NKW-361/Izibra-sezone-iz-URL-parametrov
                // However the problem could also be that this here is called before the availableSeasons are actually set
                if (availableSeasons.includes(parsedSeason) || (seasonParam === 'ALL' && allowAllSeasons)) {
                    initialSeason = parsedSeason;
                    seasonSelectedManually.current = true;
                }
            }
            if (season !== null && availableSeasons.includes(season) && seasonSelectedManually.current) {
            }
            else {
                _setSeason(initialSeason);
            }
            isSeasonInitialized.current = true;
        }
        else {
            if (season !== null && !availableSeasons.includes(season)) {
                _setSeason(availableSeasons[0]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableSeasons]);
    // Define setSeason function
    function setSeason(newSeason, replace) {
        _setSeason(newSeason);
        seasonSelectedManually.current = true;
        setParam('season', newSeason === ALL_SEASONS ? 'ALL' : newSeason.toString(), { replace: replace });
    }
    return (_jsx(SeasonContext.Provider, __assign({ value: {
            season: season !== null && season !== void 0 ? season : (allowAllSeasons ? ALL_SEASONS : firstSeason),
            setSeason: setSeason,
            availableSeasons: availableSeasons,
        } }, { children: children })));
}
